/**
 * AnalyticsPieChart Component
 * 
 * Pie chart visualization for analytics data.
 */

import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export interface CategoryData {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
}

export interface AnalyticsPieChartProps {
  data: CategoryData[];
  selectedTab: "expenses" | "incomes" | "adjustments";
  isShowingPlaceholder: boolean;
  shouldHidePlaceholder: boolean;
  shouldAnimate: boolean;
  loading: boolean;
  formatAmount: (amount: number) => string;
  categoryData: CategoryData[];
  hasAnimatedOnMount?: boolean;
  /** When true, chart container fits parent height (no minHeight), for use in fixed-height cards e.g. mobile */
  fitContainer?: boolean;
}

/**
 * Pie chart component for analytics
 */
export const AnalyticsPieChart: React.FC<AnalyticsPieChartProps> = ({
  data,
  selectedTab,
  isShowingPlaceholder,
  shouldHidePlaceholder,
  shouldAnimate,
  loading,
  formatAmount,
  categoryData,
  hasAnimatedOnMount = false,
  fitContainer = false,
}) => {
  const theme = useTheme();
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const tooltipBg = theme.palette.background.paper;
  const tooltipBorder = theme.palette.divider;
  const tooltipText = theme.palette.text.primary;

  const containerSx = {
    flex: fitContainer ? "1 1 auto" : "1 1 60%",
    minHeight: fitContainer ? 200 : 400,
    minWidth: 0, // avoid Recharts "width(-1)" when flex container has no size yet (e.g. deployed first paint)
    height: "100%",
    width: "100%",
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    p: fitContainer ? 1 : 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none" as const,
    "& svg": { userSelect: "none" },
    "& *": { userSelect: "none", outline: "none", "&:focus": { outline: "none" }, "&:active": { outline: "none" } },
  };

  // Show placeholder if no data or showing placeholder
  if (isShowingPlaceholder && data.length === 0) {
    return (
      <Box sx={containerSx}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...containerSx, alignItems: "stretch", justifyContent: "stretch" }}>
      <Box sx={{ width: "100%", height: "100%", minWidth: 1, minHeight: 1, flex: 1 }}>
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={fitContainer ? { width: 300, height: 280 } : undefined}
        >
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            isAnimationActive={shouldAnimate && !loading}
            animationBegin={0}
            animationDuration={fitContainer ? 400 : 1200}
            animationEasing={fitContainer ? "linear" : "ease-out"}
            labelLine={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
              // Hide labelLine for placeholder data
              if (isShowingPlaceholder || midAngle === undefined || !outerRadius) {
                return <line x1={cx} y1={cy} x2={cx} y2={cy} stroke="transparent" />;
              }
              // Hide labelLine for segments smaller than 5% to prevent overlap
              const percentage = (percent || 0) * 100;
              if (percentage < 5) {
                return <line x1={cx} y1={cy} x2={cx} y2={cy} stroke="transparent" />;
              }
              const RADIAN = Math.PI / 180;
              const radius = outerRadius + 10;
              const x = cx + radius * Math.cos(-midAngle * RADIAN);
              const y = cy + radius * Math.sin(-midAngle * RADIAN);
              const labelRadius = outerRadius + 30;
              const labelX = cx + labelRadius * Math.cos(-midAngle * RADIAN);
              const labelY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
              
              return (
                <line
                  x1={x}
                  y1={y}
                  x2={labelX}
                  y2={labelY}
                  stroke={textSecondary}
                  strokeWidth={1}
                />
              );
            }}
            label={({ cx, cy, midAngle, innerRadius, outerRadius, name, percent, value }) => {
              // Hide labels for placeholder data
              if (isShowingPlaceholder || midAngle === undefined || !outerRadius) return null;
              
              // Hide labels for segments smaller than 5% to prevent overlap and crowding
              const percentage = (percent || 0) * 100;
              if (percentage < 5) {
                return null;
              }
              
              const RADIAN = Math.PI / 180;
              // Position label further outside the pie (name on first line, % on second)
              const labelRadius = outerRadius + 35;
              const labelX = cx + labelRadius * Math.cos(-midAngle * RADIAN);
              const labelY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
              const textAnchor = labelX >= cx ? "start" : "end";
              const percentText = `${percentage.toFixed(1)}%`;

              return (
                <g>
                  <text
                    x={labelX}
                    y={labelY - 6}
                    textAnchor={textAnchor}
                    fill={textPrimary}
                    fontSize={12}
                    fontWeight={500}
                    dominantBaseline="middle"
                  >
                    {name}
                  </text>
                  <text
                    x={labelX}
                    y={labelY + 8}
                    textAnchor={textAnchor}
                    fill={textSecondary}
                    fontSize={11}
                    dominantBaseline="middle"
                  >
                    {percentText}
                  </text>
                </g>
              );
            }}
            startAngle={90}
            endAngle={450}
            outerRadius="65%"
            innerRadius={isShowingPlaceholder ? "60%" : 0}
            fill="#8884d8"
            dataKey="value"
            stroke={shouldHidePlaceholder ? "transparent" : (isShowingPlaceholder ? "#fff" : "none")}
            strokeWidth={isShowingPlaceholder ? 2 : 0}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color}
                opacity={shouldHidePlaceholder ? 0 : 1}
              />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length || isShowingPlaceholder) return null;
              const item = payload[0];
              const name = label ?? item?.name ?? "";
              const rawVal = item?.value;
              const displayVal =
                selectedTab === "adjustments" && name
                  ? formatAmount(categoryData.find(e => e.name === name)?.value ?? 0)
                  : formatAmount(typeof rawVal === "number" ? rawVal : 0);
              return (
                <Box
                  sx={{
                    backgroundColor: tooltipBg,
                    border: `1px solid ${tooltipBorder}`,
                    borderRadius: 1,
                    color: tooltipText,
                    p: 1.25,
                    px: 1.75,
                    boxShadow: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "inherit", fontWeight: 600 }}>
                    {name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "inherit" }}>
                    {displayVal}
                  </Typography>
                </Box>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      </Box>
    </Box>
  );
};
